// Side-effect import: <std-accordion> must be registered before this
// hydrates one, and the class is otherwise referenced only as a type.
import "@tento-ui/components/accordion";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Typography } from "@tento-ui/ui/typography";
import type { Accordion } from "@tento-ui/components/accordion";

/** `thought` — the agent's reasoning, collapsed by default.
 *
 * Every vendor protocol separates reasoning from prose (ACP's
 * `agent_thought_chunk`, OpenAI's reasoning items, Vercel's
 * `ReasoningUIPart`) and so does this family, for a reason that is about
 * reading rather than data: a reader skimming for what happened wants to
 * skip it, and a reader auditing a decision wants all of it. Collapsed by
 * default with a one-line summary serves both; rendering it as prose
 * serves neither.
 *
 * Disclosure is local view state, not an application decision — expanding
 * a thought has no effect on the session, which is why this stays a
 * presentational primitive and `ask` remains the only interactive one.
 *
 * ## It is an accordion, and it always was
 *
 * This component used to hand-roll the disclosure: a button, a chevron div,
 * an `expanded` attribute, a click handler, a rail derived from the
 * chevron's optical centre. Every one of those lines was `std-accordion`
 * rewritten from memory, and the four alignment verdicts this file collected
 * were the drift you get for doing that — a hand-kept column agrees with its
 * neighbours only until one of them moves.
 *
 * `turn.tool` was converted first, on the review that said so: "this is
 * essentially a smaller accordian. can we reuse the accordian and just tweak
 * its values with css variables." That verdict was never about `tool`. This
 * is the same object — a disclosure control, a header, a body that opens —
 * so it binds the same primitive and tunes it with the same variables, which
 * is also what puts a thought's words in the SAME column as a tool call's
 * when a transcript stacks them. */
@component({ name: "std-turn-thought" })
export class TurnThought extends LonaWebComponent {
  private $row: Accordion = this.hydrate("row");

  static makeWith(props: TurnThought.Props): TurnThought {
    return (TurnThought.make() as TurnThought).bind(props);
  }

  bind(props: TurnThought.Props): this {
    void this.$row.bind({
      items: [{
        header: TurnThought.buildSummary(
          props.summary ?? TurnThought.DEFAULT_SUMMARY,
        ),
        content: TurnThought.buildBody(props.text),
      }],
      expandedIndexes: (props.expanded ?? false) ? [0] : [],
    });
    return this;
  }

  /** The collapsed line. Built as an element and styled INLINE because it
   * goes into the accordion's shadow root, where a stylesheet written here
   * cannot reach it. Typography's sheet IS in every shadow root, so the ramp
   * class still resolves and only the values travel. */
  private static buildSummary(summary: string): HTMLElement {
    const $summary = Typography.small(summary);
    Object.assign($summary.style, {
      flex: "1",
      minWidth: "0",
      fontStyle: "italic",
      color: "var(--text-ter, #8b93a7)",
    });
    return $summary;
  }

  /** The reasoning, in the row's own word column and with no rail.
   *
   * No rail for the reason `turn.tool` lost its: a vertical rule needs
   * something on both sides of it to earn its ink, and a disclosed body has
   * nothing to its left but the indent. Revised on review there: "remove the
   * divider between tool calls. code-block has too much padding-left and is
   * not aligned." The same words applied here before anyone had to say them
   * twice.
   *
   * The geometry is on the element, not in --accordion-content-padding,
   * because an ELEMENT handed to the accordion as content BECOMES the padded
   * box — `renderContent` puts `.accordion-content-custom` on the element
   * itself — so the knob and this element's own padding are the same
   * declaration and inline wins. */
  private static buildBody(text: string): HTMLElement {
    const $body = Typography.small(text);
    Object.assign($body.style, {
      margin: "2px 0 4px calc(var(--turn-chevron-width, 14px) + 6px)",
      padding: "0",
      color: "var(--text-sec, #6a7186)",
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
    });
    $body.style.setProperty("--line-height-ratio", "1.55");
    return $body;
  }

  static $styles = [
    css`
      /* width, not shrink-to-fit. Revised on review: "layout shift on
         collapse" - measured at 350.84px sideways. Expanded, the body is a
         full-width block and the host filled its column; collapsed, only the
         toggle was left, the host shrank to that one short line, and any
         centring parent moved the whole row. A disclosure must only ever
         change HEIGHT. */
      :host {
        display: block;
        width: 100%;
        min-width: 0;
        /* The same three the tool row declares, and deliberately the same
           values: a thought and a tool call are rows of one transcript, so
           their chevron columns, their word columns and their highlights
           have to be one set of numbers rather than two that agree today. */
        --turn-row-pad-y: 3px;
        --turn-row-bleed-x: 6px;
        --turn-row-hover-inset-y: 1px;
      }
      /* Every value this row needs, and nothing drawn by hand. The
         accordion's own defaults are a page section's - 12px of header
         padding, an icon column sized for a heading, 36px of content indent.
         A transcript row is a denser object, and that difference is the only
         thing this block says. */
      #row {
        --accordion-header-padding: var(--turn-row-pad-y) 0;
        --accordion-header-gap: 6px;
        --accordion-icon-size: var(--turn-chevron-width, 14px);
        --accordion-icon-svg-size: var(--turn-chevron-size, 10px);
        /* Zero, because the body IS the padded box - see buildBody - and
           carries its own indent. */
        --accordion-content-padding: 0px;
        --accordion-header-hover-inset: var(--turn-row-hover-inset-y)
          calc(-1 * var(--turn-row-bleed-x));
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-accordion id="row"></std-accordion>
  `;
}

export namespace TurnThought {
  export const DEFAULT_SUMMARY = "Thought";

  export type Props = {
    text: string;
    /** The collapsed line. Defaults to a neutral label. */
    summary?: string;
    expanded?: boolean;
  };
}
